import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { money, fmtDate, TREATMENTS } from "../lib/format";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Plus, CalendarClock, Wand2, CheckCircle2, Pencil, Trash2, AlertTriangle } from "lucide-react";

const FREQ = { monthly: "Monthly", quarterly: "Quarterly", semiannual: "6-Monthly", annually: "Annually", "one-off": "One-off" };
const MULT = { monthly: 12, quarterly: 4, semiannual: 2, annually: 1, "one-off": 0 };

const emptyForm = () => ({
  name: "", amount: "", frequency: "annually",
  next_due: new Date().toISOString().slice(0, 10), category: "", treatment: "standard", notes: "",
});

export default function FixedCostsPage() {
  const [items, setItems] = useState([]);
  const [profile, setProfile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    setLoading(true);
    const [r, p, c] = await Promise.all([api.get("/recurring"), api.get("/profile"), api.get("/categories")]);
    setItems(r.data);
    setProfile(p.data);
    setCategories(c.data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it, amount: String(it.amount) }); setOpen(true); };

  const save = async () => {
    const payload = { ...form, amount: parseFloat(form.amount) || 0, active: true };
    if (!payload.name || !payload.amount) return;
    if (editing) await api.put(`/recurring/${editing.id}`, payload);
    else await api.post("/recurring", payload);
    toast.success(editing ? "Fixed cost updated" : "Fixed cost added");
    setOpen(false);
    load();
  };

  const remove = async (id) => { await api.delete(`/recurring/${id}`); toast.success("Removed"); load(); };
  const markPaid = async (id) => {
    const { data } = await api.post(`/recurring/${id}/pay`);
    toast.success(`Logged as expense · next due ${fmtDate(data.next_due)}`);
    load();
  };
  const loadDefaults = async () => {
    const { data } = await api.post("/recurring/seed-defaults");
    toast.success(data.added ? `Added ${data.added} ${data.entity_type === "company" ? "company" : "sole trader"} defaults` : "Defaults already present");
    load();
  };

  const today = new Date().toISOString().slice(0, 10);
  const active = items.filter((i) => i.active !== false);
  const annualTotal = active.reduce((s, i) => s + i.amount * (MULT[i.frequency] ?? 0), 0);
  const nextDue = active.filter((i) => i.frequency !== "one-off" || i.next_due >= today)
    .slice().sort((a, b) => a.next_due.localeCompare(b.next_due))[0];
  const overdueCount = active.filter((i) => i.next_due < today).length;
  const entityLabel = profile?.entity_type === "company" ? "Company (Pty Ltd)" : "Sole Trader";
  const catOptions = Array.from(new Set([
    ...categories.filter((c) => c.type === "expense").map((c) => c.name),
    "Insurance", form.category,
  ].filter(Boolean)));

  const dueBadge = (d) => {
    const days = Math.ceil((new Date(d) - new Date(today)) / 86400000);
    if (days < 0) return { txt: `${Math.abs(days)}d overdue`, cls: "text-rose-700 bg-rose-50 border-rose-200" };
    if (days <= 14) return { txt: `Due in ${days}d`, cls: "text-amber-700 bg-amber-50 border-amber-200" };
    return { txt: `In ${days}d`, cls: "text-emerald-700 bg-emerald-50 border-emerald-200" };
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Fixed Costs</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Recurring obligations for your <span className="font-medium text-foreground">{entityLabel}</span> setup — track amounts and due dates.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadDefaults} data-testid="load-defaults-btn" className="gap-2">
            <Wand2 className="w-4 h-4" /> Load {profile?.entity_type === "company" ? "company" : "sole trader"} defaults
          </Button>
          <Button onClick={openNew} data-testid="add-fixed-cost-btn" className="gap-2"><Plus className="w-4 h-4" /> Add fixed cost</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="fc-annual-total">
          <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Annualised fixed costs</div>
          <div className="num text-2xl font-bold mt-2">{money(annualTotal)}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Next payment</div>
          <div className="num text-2xl font-bold mt-2">{nextDue ? money(nextDue.amount) : "—"}</div>
          <div className="text-xs text-muted-foreground mt-1 truncate">{nextDue ? `${nextDue.name} · ${fmtDate(nextDue.next_due)}` : "Nothing scheduled"}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Overdue</div>
          <div className={`num text-2xl font-bold mt-2 ${overdueCount ? "text-rose-600" : ""}`}>{overdueCount}</div>
          <div className="text-xs text-muted-foreground mt-1">Payments past their due date</div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50 hover:bg-secondary/50">
                <TableHead>Cost</TableHead>
                <TableHead className="hidden md:table-cell">Category</TableHead>
                <TableHead>Frequency</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Next due</TableHead>
                <TableHead className="w-32 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                  No fixed costs yet. Click "Load defaults" to add common {entityLabel} obligations.
                </TableCell></TableRow>
              ) : items.map((it) => {
                const b = dueBadge(it.next_due);
                const tr = TREATMENTS[it.treatment];
                return (
                  <TableRow key={it.id} className="row-hover" data-testid={`fixed-cost-row-${it.id}`}>
                    <TableCell>
                      <div className="font-medium flex items-center gap-2">
                        {it.name}
                        {it.next_due < today && <AlertTriangle className="w-4 h-4 text-rose-500" />}
                      </div>
                      {it.notes && <div className="text-xs text-muted-foreground max-w-xs truncate">{it.notes}</div>}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-sm">{it.category || "—"}</span>
                      {tr && <span className={`ml-2 text-xs px-1.5 py-0.5 rounded border ${tr.color}`}>{tr.short}</span>}
                    </TableCell>
                    <TableCell className="text-sm">{FREQ[it.frequency] || it.frequency}</TableCell>
                    <TableCell className="num text-right font-semibold">{money(it.amount)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span className="num text-xs">{fmtDate(it.next_due)}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-md border w-fit ${b.cls}`}>{b.txt}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <button onClick={() => markPaid(it.id)} title="Mark paid (log expense)" data-testid={`pay-fixed-${it.id}`}
                          className="p-1.5 rounded-md hover:bg-emerald-50 text-muted-foreground hover:text-emerald-600 transition-colors">
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => openEdit(it)} data-testid={`edit-fixed-${it.id}`}
                          className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => remove(it.id)} data-testid={`delete-fixed-${it.id}`}
                          className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="fixed-cost-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit" : "New"} fixed cost</DialogTitle>
            <DialogDescription>Recurring obligation with an amount and next due date.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3.5 py-1">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="fc-name" placeholder="e.g. ASIC Annual Review Fee" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount (incl. GST)</Label>
                <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="mt-1.5" data-testid="fc-amount" placeholder="0.00" />
              </div>
              <div>
                <Label>Next due</Label>
                <Input type="date" value={form.next_due} onChange={(e) => set("next_due", e.target.value)} className="mt-1.5" data-testid="fc-next-due" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Frequency</Label>
                <Select value={form.frequency} onValueChange={(v) => set("frequency", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="fc-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(FREQ).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>GST treatment</Label>
                <Select value={form.treatment} onValueChange={(v) => set("treatment", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="fc-treatment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TREATMENTS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category || undefined} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1.5" data-testid="fc-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {catOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes <span className="text-muted-foreground">(optional)</span></Label>
              <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1.5" data-testid="fc-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="fc-save">{editing ? "Save changes" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
